import { registerLocaleData } from "@angular/common";
import localeIt from "@angular/common/locales/it";
import { BrowserModule } from "@angular/platform-browser";
import { ErrorHandler, LOCALE_ID, NgModule } from "@angular/core";
import { RouterModule } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgxPermissionsModule } from "ngx-permissions";

import { SpinnerComponent } from "./shared/spinner.component";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatRadioModule } from "@angular/material/radio";
import { MatCardModule } from "@angular/material/card";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { LoggerModule, NgxLoggerLevel } from "ngx-logger";
import { environment } from "../environments/environment";
import { NgScrollbarModule } from "ngx-scrollbar";
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
} from "@ngx-translate/core";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from "@angular/material/core";
import { TablerIconsModule } from "angular-tabler-icons";
import * as TablerIcons from "angular-tabler-icons/icons";
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from "@angular/material-moment-adapter";

import { ExternalLinkManagerModule } from "./shared/components/external-link-manager/external-link-manager.module";
import { AppRoutingModule } from "./app.routing";
import { ErrorHandlerService } from "./core/services/error-handler.service";
import { combineLatest, map, Observable, of } from "rxjs";
import { defaults } from "./app.config";
import { JWT_OPTIONS, JwtHelperService } from "@auth0/angular-jwt";
import { CoreModule } from "./core/core.module";
import { ServiceWorkerModule } from "@angular/service-worker";
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from "@angular/material/snack-bar";
import { DatePipe } from "@angular/common";
import { ITALIAN_DATE_FORMATS } from "./shared/utils/custom-date-adapter";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { MaterialModule } from "./material.module";
import { OverlayComponent } from "./shared/components/overlay/overlay.component";

registerLocaleData(localeIt);

// Genera un hash randomico all'avvio dell'applicazione
const randomCacheBust = Math.random().toString(36).substring(2, 12);

function HttpLoaderFactory(http: HttpClient): any {
  return new CustomTranslateLoader(http, randomCacheBust);
}

class CustomTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private cacheBust: string,
  ) {}
  private path = "./assets/i18n";
  private brandPath = "./brand/assets/i18n";

  getTranslation(lang: string): Observable<any> {
    // Usa il random hash per cache busting
    const cacheBust = this.cacheBust;

    const defaultTranslation$ = this.http.get(
      `${this.path}/common/${lang}.json?cacheBust=${cacheBust}`,
    );

    let customTranslation$ = of({});

    return combineLatest([defaultTranslation$, customTranslation$]).pipe(
      map(([defaultTranslation, customTranslation]) => {
        return { ...defaultTranslation, ...customTranslation };
      }),
    );
  }
}

// Custom handler per traduzioni mancanti
export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams): string {
    const translateService = params.translateService;

    // Funzione per accedere a chiavi nidificate
    const getNestedValue = (obj: any, path: string): any => {
      return path.split(".").reduce((acc, part) => acc && acc[part], obj);
    };

    // Tenta di trovare la traduzione nella lingua di default
    const defaultLang = defaults.defaultLanguage;
    if (defaultLang && defaultLang !== translateService.currentLang) {
      const defaultTranslations = translateService.translations[defaultLang];
      const defaultTranslation = getNestedValue(
        defaultTranslations,
        params.key,
      );
      if (defaultTranslation) {
        return defaultTranslation;
      }
    }

    // Fallback finale: restituisci la chiave
    return params.key;
  }
}

@NgModule({
  declarations: [AppComponent, SpinnerComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    MaterialModule,
    OverlayComponent,
    ExternalLinkManagerModule,
    TablerIconsModule.pick(TablerIcons),
    LoggerModule.forRoot({
      level: environment.production
        ? NgxLoggerLevel.ERROR
        : NgxLoggerLevel.DEBUG,
    }),
    NgScrollbarModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NgxPermissionsModule.forRoot(),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: true,
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
  providers: [
    DatePipe,
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    { provide: LOCALE_ID, useValue: "it" },
    { provide: MAT_DATE_LOCALE, useValue: "it-IT" },
    { provide: MAT_DATE_FORMATS, useValue: ITALIAN_DATE_FORMATS },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 5000 }, // Configura la durata predefinita
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MatPaginatorIntl },
    JwtHelperService,
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
